import {
  DEFAULT_DASHBOARD_LIMITS,
  LIMIT_KEYS,
  LIMIT_LABELS,
  isRateLimit,
  type DashboardLimits,
  type LimitKey,
  type WriteRejection,
} from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";

type Draft = Record<LimitKey, { amber: string; red: string }>;

function toDraft(limits: DashboardLimits): Draft {
  return Object.fromEntries(
    LIMIT_KEYS.map((key) => [
      key,
      { amber: String(limits[key].amber), red: String(limits[key].red) },
    ]),
  ) as Draft;
}

function fromDraft(draft: Draft): DashboardLimits {
  return Object.fromEntries(
    LIMIT_KEYS.map((key) => [
      key,
      { amber: Number(draft[key].amber), red: Number(draft[key].red) },
    ]),
  ) as DashboardLimits;
}

function message(rejection: WriteRejection): string {
  return rejection.message;
}

/**
 * The form: an amber and a red limit for every number the dashboard colours.
 * A figure above the amber limit turns amber; above the red limit, red.
 */
function Form({ saved }: { saved: DashboardLimits }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => toDraft(saved));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: () => data.dashboard.saveLimits(fromDraft(draft)),
    onSuccess: (result) => {
      if (result.ok) {
        setFields({});
        setFailure(null);
        setDone(true);
        queryClient.setQueryData(["dashboard", "limits"], result.data);
        // The dashboard's colours are read from the same limits.
        void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      } else {
        setDone(false);
        setFailure(message(result.rejection));
        setFields(
          result.rejection.kind === "invalid"
            ? (result.rejection.fields ?? {})
            : {},
        );
      }
    },
  });

  const changed = LIMIT_KEYS.some(
    (key) =>
      draft[key].amber !== String(saved[key].amber) ||
      draft[key].red !== String(saved[key].red),
  );

  function edit(key: LimitKey, which: "amber" | "red", value: string) {
    setDraft((current) => ({
      ...current,
      [key]: { ...current[key], [which]: value },
    }));
    setDone(false);
  }

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle>Dashboard colours</CardTitle>
        <CardDescription>
          A card or rate turns amber when it is above its amber limit and red
          when it is above its red limit. A card with a count is amber above
          zero until you say otherwise. Rates are percentages. The cards that
          wait on the Confirmed stage have limits too, ready for when they get a
          number.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[28rem] text-sm">
            <thead>
              <tr className="text-muted-foreground border-b text-left text-xs">
                <th scope="col" className="py-2 pr-3 font-normal">
                  Number
                </th>
                <th scope="col" className="w-28 py-2 pr-3 font-normal">
                  Amber above
                </th>
                <th scope="col" className="w-28 py-2 font-normal">
                  Red above
                </th>
              </tr>
            </thead>
            <tbody>
              {LIMIT_KEYS.map((key) => {
                const error = fields[key];
                const errorId = `limit-${key}-error`;
                return (
                  <tr key={key} className="border-b align-top last:border-0">
                    <th scope="row" className="py-2 pr-3 text-left font-normal">
                      {LIMIT_LABELS[key]}
                      {error ? (
                        <p
                          id={errorId}
                          role="alert"
                          className="text-destructive text-xs"
                        >
                          {error}
                        </p>
                      ) : null}
                    </th>
                    {(["amber", "red"] as const).map((which) => (
                      <td key={which} className="py-1.5 pr-3 last:pr-0">
                        <Input
                          type="number"
                          inputMode={isRateLimit(key) ? "decimal" : "numeric"}
                          min={0}
                          step={isRateLimit(key) ? "0.1" : "1"}
                          value={draft[key][which]}
                          aria-label={`${LIMIT_LABELS[key]}, ${which} above`}
                          aria-invalid={error ? true : undefined}
                          aria-describedby={error ? errorId : undefined}
                          disabled={save.isPending}
                          className="h-9 pointer-coarse:h-11"
                          onChange={(event) => {
                            edit(key, which, event.target.value);
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {failure && Object.keys(fields).length === 0 ? (
          <p role="alert" className="text-destructive text-sm">
            {failure}
          </p>
        ) : null}
        {failure && Object.keys(fields).length > 0 ? (
          <p role="alert" className="text-destructive text-sm">
            {failure} Nothing was saved.
          </p>
        ) : null}
        {done ? (
          <p
            role="status"
            className="text-sm text-emerald-700 dark:text-emerald-300"
          >
            Saved. The dashboard uses these now.
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={!changed || save.isPending}
            className="pointer-coarse:h-11"
            onClick={() => {
              save.mutate();
            }}
          >
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={save.isPending}
            className="pointer-coarse:h-11"
            onClick={() => {
              setDraft(toDraft(DEFAULT_DASHBOARD_LIMITS));
              setFields({});
              setFailure(null);
              setDone(false);
            }}
          >
            Reset to defaults
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Settings > Dashboard (#528): where the dashboard's amber and red limits are edited. */
export function DashboardSettings() {
  const data = useDataSource();
  const query = useQuery({
    queryKey: ["dashboard", "limits"],
    queryFn: () => data.dashboard.getLimits(),
  });

  if (query.isPending) return <TableSkeleton rows={4} />;
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  return <Form saved={query.data} />;
}
